import { HttpContextToken, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { from } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { environment } from '@env/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /**
   * Opt-out for non-critical authenticated requests.
   * Used by layout/bootstrap calls that may legitimately return 401/403 without
   * meaning the whole application session is invalid.
   */
  static readonly SKIP_AUTH_ERROR_REDIRECT = new HttpContextToken<boolean>(() => false);

  private static readonly AUTH_FLOW_ROUTES = ['/auth/callback', '/consent', '/access-denied', '/sign-up'];

  private isAuthFlowRoute(url: string): boolean {
    return AuthInterceptor.AUTH_FLOW_ROUTES.some(route => url.startsWith(route));
  }

  // Prevent multiple concurrent refresh calls: queue waiters on a BehaviorSubject
  private isRefreshing = false;
  private refreshToken$ = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const token = this.authService.getAccessToken();

    const apiUrls = [
      environment.url_resources_api,
      environment.url_general_api,
      environment.url_switch_api,
      environment.url_document_api,
      environment.url_meteo_api,
      environment.url_audit_api,
      environment.url_uas_api,
      environment.url_events_api,
      environment.url_location_api,
      environment.url_catalog_api,
      environment.url_infrastructure_events_api,
      environment.API_URL,
      environment.API_URL_TRACKING,
    ];

    let authReq = req;

    if (token && this.isApiRequest(req.url, apiUrls) && !req.headers.has('Authorization')) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {

        if (this.isConsentRequired(error)) {
          if (!this.router.url.startsWith('/consent')) {
            this.router.navigate(['/consent']);
          }
          return throwError(() => error);
        }

        if (error.status === 401 || error.status === 403) {
          const skipAuthRedirect = authReq.context.get(AuthInterceptor.SKIP_AUTH_ERROR_REDIRECT);
          if (skipAuthRedirect) {
            return throwError(() => error);
          }

          const currentToken = this.authService.getAccessToken();
          const expired = !currentToken || this.isTokenExpired(currentToken);

          if (expired) {
            if (!currentToken) {
              if (this.isAuthFlowRoute(this.router.url)) {
                console.log('[AUTH-LOOP-DEBUG] interceptor: no-token 401/403 while on auth-flow route, skipping login()', this.router.url); // TEMP DEBUG
                return throwError(() => error);
              }
              this.authService.login(this.router.url);
              return EMPTY;
            }
            return this.handleExpiredToken(req, next);
          }

          if (error.status === 403) {
            this.handle403(error);
          } else {
            this.handle401(error);
          }
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Reactive refresh fallback: called when a 401 arrives and the access token
   * is already expired. Attempts a token refresh; on success retries the original
   * request with the new token. Concurrent requests wait for the single refresh
   * to complete via refreshToken$ subject.
   */
  private handleExpiredToken(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshToken$.next(null);

      return from(this.authService.refreshToken()).pipe(
        switchMap(() => {
          const newToken = this.authService.getAccessToken()!;
          this.isRefreshing = false;
          this.refreshToken$.next(newToken);

          const retryReq = req.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` }
          });
          return next.handle(retryReq);
        }),
        catchError(() => {
          this.isRefreshing = false;
          this.authService.clear();
          if (!this.isAuthFlowRoute(this.router.url)) {
            this.authService.login(this.router.url);
          }
          return EMPTY;
        })
      );
    }

    // Another request already triggered the refresh — wait for it to complete
    return this.refreshToken$.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap(newToken => {
        const retryReq = req.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` }
        });
        return next.handle(retryReq);
      })
    );
  }

  private isApiRequest(url: string, apiUrls: string[]): boolean {
    // Filter out empty placeholders (e.g. an environment with no deployment yet
    // for a given base URL) — url.includes('') is always true and would attach
    // the Bearer token to every outgoing request.
    return apiUrls.filter(Boolean).some(base => url.includes(base));
  }

  private handle401(error: HttpErrorResponse): void {
    // This method is only called when the access token is NOT expired (expired
    // tokens are handled separately by handleExpiredToken). A 401 with a valid
    // token always means a backend permission issue, never a token issue.
    // Redirecting to login here would cause an infinite loop: the same 401
    // would repeat after Keycloak's SSO immediately re-issues a token.
    this.navigateToAccessDenied();
  }

  private handle403(error: HttpErrorResponse): void {
    this.navigateToAccessDenied();
  }

  private isConsentRequired(error: HttpErrorResponse): boolean {
    const code = error.error?.error?.code ?? error.error?.code;
    return error.status === 403 && code === 'consent_required';
  }

  private navigateToAccessDenied(): void {
    if (this.router.url !== '/access-denied') {
      this.router.navigate(['/access-denied']);
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payloadB64 = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
      if (!payloadB64) {
        return true;
      }

      const payload = JSON.parse(atob(payloadB64));
      const exp = Number(payload?.exp);
      if (!Number.isFinite(exp)) {
        return true;
      }

      return exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  }
}
